<template>
  <div class="addFormDubEditorTab">
    <!-- Step sub-tabs: Primary / Secondary / QC -->
    <div class="addFormDubEditorTab__tabs" v-if="dubEditorData">
      <button
        class="dub-tab-btn"
        :class="{
          active: dubEditorTab === 'Primary',
          'dub-tab-btn--rounded-left': assignmentForm.secondary,
          'dub-tab-btn--rounded': !assignmentForm.secondary,
        }"
        @click.prevent="setDubEditorTab('Primary')"
      >
        <v-icon
          v-if="dubEditorData.primaryFragments && dubEditorData.primaryFragments.complete"
          class="dub-tab-btn__icon"
          :class="{ active: dubEditorTab === 'Primary' }"
        >
          mdi-check
        </v-icon>
        <div :class="{ 'ml-2': dubEditorTab === 'Primary' }">
          <p class="dub-tab-btn__label">PRIMARY</p>
          <p class="dub-tab-btn__name">{{ dubEditorData.primaryFragments.staffName ?? '' }}</p>
        </div>
      </button>

      <button
        class="dub-tab-btn"
        :class="{
          active: dubEditorTab === 'Secondary',
          'dub-tab-btn--square': assignmentForm.qc,
          'dub-tab-btn--rounded-right': !assignmentForm.qc,
        }"
        v-if="assignmentForm.secondary"
        @click.prevent="setDubEditorTab('Secondary')"
      >
        <v-icon
          v-if="dubEditorData.secondaryFragments && dubEditorData.secondaryFragments.complete"
          class="dub-tab-btn__icon"
          :class="{ active: dubEditorTab === 'Secondary' }"
        >
          mdi-check
        </v-icon>
        <div :class="{ 'ml-2': dubEditorTab === 'Secondary' }">
          <p class="dub-tab-btn__label">SECONDARY</p>
          <p class="dub-tab-btn__name">{{ dubEditorData.secondaryFragments.staffName ?? '' }}</p>
        </div>
      </button>

      <button
        class="dub-tab-btn dub-tab-btn--rounded-right"
        :class="{ active: dubEditorTab === 'QC' }"
        v-if="assignmentForm.qc"
        @click.prevent="setDubEditorTab('QC')"
      >
        <v-icon
          v-if="dubEditorData.qcFragments && dubEditorData.qcFragments.complete"
          class="dub-tab-btn__icon"
          :class="{ active: dubEditorTab === 'QC' }"
        >
          mdi-check
        </v-icon>
        <div :class="{ 'ml-2': dubEditorTab === 'QC' }">
          <p class="dub-tab-btn__label">QC</p>
          <p class="dub-tab-btn__name">{{ dubEditorData.qcFragments.staffName ?? '' }}</p>
        </div>
      </button>
    </div>

    <!-- Fragments -->
    <div class="addFormDubEditorTab__fragments" v-if="dubEditorData">
      <div
        class="addFormDubEditorTab__fragments--item mb-15px"
        v-for="(fragment, index) in dubEditorDataToShow && dubEditorDataToShow.fragments"
        :key="`${dubEditorTab}-${fragment.id}-${index}`"
      >
        <RichTextEditor
          :title="fragmentTitle(fragment)"
          :value="fragment.text || ''"
          :isReadOnly="isFragmentReadOnly"
          @input="onFragmentInput(fragment, $event)"
        />
      </div>
    </div>

    <!-- Empty state -->
    <p v-else-if="dubChecked" class="addFormDubEditorTab__empty">
      {{ emptyMessage }}
    </p>

    <!-- Actions -->
    <div
      class="addFormDubEditorTab__actions d-flex align-center"
      :class="{ 'justify-end': !changesWereAdded }"
      v-if="dubEditorData && dubIsReal"
    >
      <v-alert
        v-if="checkIfShowResubmitAlert"
        class="mb-0 d-flex addFormDubEditorTab__actions--alert"
        icon="mdi-information-outline"
        text
        dense
        type="info"
      >
        <v-row align="center">
          <v-col class="grow">There are changes that were not submitted</v-col>
          <v-col class="shrink pa-1">
            <v-btn color="#1976D2" plain :loading="dubLoading" @click="discardChanges">
              discard
            </v-btn>
          </v-col>
          <v-col class="shrink pa-1">
            <v-btn color="#FC7777" plain :loading="dubLoading" @click="confirmationDubReSubmition = true">
              Resubmit
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>
      <v-alert
        v-else-if="checkIfShowSaveDraftAlert"
        class="mb-0 d-flex addFormDubEditorTab__actions--alert"
        icon="mdi-information-outline"
        text
        dense
        type="info"
      >
        <v-row align="center">
          <v-col class="grow">There are unsaved changes</v-col>
          <v-col class="shrink pa-1">
            <v-btn color="#1976D2" plain :loading="dubLoading" @click="handleDubProcessing('SaveDraft')">
              Save draft
            </v-btn>
          </v-col>
          <v-col class="shrink pa-1">
            <v-btn color="#FC7777" plain :loading="dubLoading" @click="discardChanges">
              discard
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>
      <v-alert
        v-else-if="prevIsNotReady && dubEditorTab !== 'Primary'"
        class="mb-0 d-flex addFormDubEditorTab__actions--alert"
        icon="mdi-information-outline"
        text
        dense
        type="info"
      >
        <v-row align="center">
          <v-col class="grow">
            {{ user.user.userRole !== 'STAFF' ? "The previous person in the workflow" : dubEditorTab === "QC" ? "Secondary" : "Primary" }} has not finished the texts yet
          </v-col>
        </v-row>
      </v-alert>

      <v-btn
        v-if="!checkIfShowResubmitButton && !checkIfShowResubmitAlert"
        color="#1976D2"
        class="mx-3 white--text"
        :loading="dubLoading"
        :disabled="checkIfDisableSaveButton"
        @click="confirmationDubSubmition = true"
      >
        {{ changesWereAdded ? "Save & Submit" : "Submit" }}
      </v-btn>
      <v-btn
        v-if="checkIfShowResubmitButton"
        color="#FF5252"
        class="mx-3 white--text"
        :loading="dubLoading"
        @click="confirmationDubUnSubmition = true"
      >
        unsubmit
      </v-btn>
    </div>

    <!-- Submit confirmation -->
    <v-dialog
      max-width="500"
      v-model="confirmationDubSubmition"
      @click:outside="confirmationDubSubmition = false"
      @keydown.esc="confirmationDubSubmition = false"
    >
      <div class="confirmation-modal">
        <p class="confirmation-modal--title">Confirm Submission</p>
        <p class="confirmation-modal--text">Are you sure you want to submit these texts? Once submitted, the assignment status will change to <span style="color: #1976D2">'{{ dubEditorTab }} Complete'</span>, and the texts will be forwarded to the next person in the workflow for further processing.</p>
        <div class="confirmation-modal--buttons d-flex align-center justify-end">
          <v-btn depressed :loading="dubLoading" @click="confirmationDubSubmition = false">Cancel</v-btn>
          <v-btn color="#1976D2" class="mx-3 white--text" :loading="dubLoading" @click="handleDubProcessing('Submit')">
            {{ changesWereAdded ? "Save & Submit" : "Submit" }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <!-- Unsubmit confirmation -->
    <v-dialog
      max-width="500"
      v-model="confirmationDubUnSubmition"
      @click:outside="confirmationDubUnSubmition = false"
      @keydown.esc="confirmationDubUnSubmition = false"
    >
      <div class="confirmation-modal">
        <p class="confirmation-modal--title">Confirm Unsubmission</p>
        <p class="confirmation-modal--text">Are you sure you want to unsubmit these texts? Once confirmed, the assignment status will change to <span style="color: #1976D2">'{{ showPrevStatus }}'</span>, and the texts will no longer be accessible to the next person in the workflow until they are resubmitted.</p>
        <div class="confirmation-modal--buttons d-flex align-center justify-end">
          <v-btn depressed :loading="dubLoading" @click="confirmationDubUnSubmition = false">Cancel</v-btn>
          <v-btn color="#FF5252" class="mx-3 white--text" :loading="dubLoading" @click="handleDubProcessing('Unsubmit')">unsubmit</v-btn>
        </div>
      </div>
    </v-dialog>

    <!-- Resubmit confirmation -->
    <v-dialog
      max-width="500"
      v-model="confirmationDubReSubmition"
      @click:outside="confirmationDubReSubmition = false"
      @keydown.esc="confirmationDubReSubmition = false"
    >
      <div class="confirmation-modal">
        <p class="confirmation-modal--title">Confirm Resubmission</p>
        <p class="confirmation-modal--text">Are you sure you want to resubmit these texts? Once confirmed, the assignment status will change to <span style="color: #1976D2">'{{ dubEditorTab }} Complete'</span>, and the resubmitted texts will override all previous changes for the next person in the workflow.</p>
        <div class="confirmation-modal--buttons d-flex align-center justify-end">
          <v-btn depressed :loading="dubLoading" @click="confirmationDubReSubmition = false">Cancel</v-btn>
          <v-btn color="#FF5252" class="mx-3 white--text" :loading="dubLoading" @click="handleDubProcessing('Resubmit')">Resubmit</v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
// vuex
import { mapActions, mapGetters } from "vuex";
// libraries
import _ from "lodash";
// internal
import { processDubEditor, getAssignmentById } from "@/utils/newDbUtils";
import RichTextEditor from "@/components/ui/RichTextEditor.vue";

// Fallback fragments used when the assignment/project has no DubEditor config,
// so the tab always shows its input fields.
const DEFAULT_DUB_FRAGMENTS = [
  { id: "default-1", fragmentName: "Review Type: Book Name", min: 0, max: 25, count: "char", order: 1 },
  { id: "default-2", fragmentName: "Custom tag", min: 0, max: 25, count: "char", order: 2 },
  { id: "default-3", fragmentName: "ALT", min: 0, max: 25, count: "char", order: 3 },
  { id: "default-4", fragmentName: "Body 1", min: 0, max: 25, count: "words", order: 4 },
  { id: "default-5", fragmentName: "Body 2", min: 0, max: 25, count: "words", order: 5 },
];

export default {
  name: "ADFormDubEditorTab",

  components: { RichTextEditor },

  data() {
    return {
      dubEditorTab: "Primary",
      dubLoading: false,
      dubEditorData: null,
      dubEditorDataClone: null,
      dubChecked: false,
      dubIsReal: false,
      confirmationDubSubmition: false,
      confirmationDubReSubmition: false,
      confirmationDubUnSubmition: false,
    };
  },

  computed: {
    ...mapGetters("assignment", ["assignmentForm"]),
    ...mapGetters("auth", ["user"]),
    emptyMessage() {
      return this.assignmentForm.id
        ? "This assignment has no DubEditor content."
        : "Select a DubEditor-enabled project to load content.";
    },
    showPrevStatus() {
      if (this.dubEditorTab === "Primary") {
        return "Assigned";
      } else if (this.dubEditorTab === "Secondary") {
        return "Primary Complete";
      } else if (this.dubEditorTab === "QC") {
        return "Secondary Complete";
      }
      return "";
    },
    dubEditorDataToShow() {
      if (this.dubEditorTab === "Primary") {
        this.dubEditorData?.primaryFragments?.fragments?.sort((a, b) => a.order - b.order);
        return this.dubEditorData?.primaryFragments;
      } else if (this.dubEditorTab === "Secondary") {
        this.dubEditorData?.secondaryFragments?.fragments?.sort((a, b) => a.order - b.order);
        return this.dubEditorData?.secondaryFragments;
      } else if (this.dubEditorTab === "QC") {
        this.dubEditorData?.qcFragments?.fragments?.sort((a, b) => a.order - b.order);
        return this.dubEditorData?.qcFragments;
      }
      return this.dubEditorData;
    },
    isFragmentReadOnly() {
      return (
        (this.user.user.userRole === "STAFF" && this.dubEditorDataToShow?.staffId !== this.user.user.id) ||
        (this.prevIsNotReady && this.dubEditorTab !== "Primary")
      );
    },
    checkIfAllowed() {
      let forbidden = false;
      if (this.dubEditorTab === "Primary" && this.user.user.userRole === "STAFF" && this.user.user.id === this.dubEditorData?.primaryFragments?.staffId) {
        forbidden = true;
      } else if (this.dubEditorTab === "Secondary" && this.user.user.userRole === "STAFF" && this.user.user.id === this.dubEditorData?.secondaryFragments?.staffId) {
        forbidden = true;
      } else if (this.dubEditorTab === "QC" && this.user.user.userRole === "STAFF" && this.user.user.id === this.dubEditorData?.qcFragments?.staffId) {
        forbidden = true;
      } else if (this.user.user.userRole !== "STAFF") {
        forbidden = true;
      }
      return forbidden;
    },
    checkIfDisableSaveButton() {
      let show = false;
      if (this.dubEditorTab === "Primary") {
        if (this.checkIfAllowed) {
          if (this.dubEditorData?.primaryFragments?.fragments.map((item) => item.text).every((item) => item === "")) {
            show = true;
            return show;
          }
          if (!this.dubEditorData?.primaryFragments?.complete) {
            show = false;
          }
        } else return true;
      } else if (this.dubEditorTab === "Secondary") {
        if (this.checkIfAllowed) {
          if (this.dubEditorData?.secondaryFragments?.fragments.map((item) => item.text).every((item) => item === "")) {
            show = true;
            return show;
          }
          if (!this.dubEditorData?.secondaryFragments?.complete) {
            show = false;
          }
          if (!this.dubEditorData?.primaryFragments?.complete) {
            return true;
          }
        } else return true;
      } else if (this.dubEditorTab === "QC") {
        if (this.checkIfAllowed) {
          if (this.dubEditorData?.qcFragments?.fragments.map((item) => item.text).every((item) => item === "")) {
            show = true;
            return show;
          }
          if (!this.dubEditorData?.qcFragments?.complete) {
            show = false;
          }
          if (!this.dubEditorData?.secondaryFragments?.complete) {
            return true;
          }
        } else return true;
      }
      return show;
    },
    checkIfShowResubmitAlert() {
      let resubmitAlert = false;
      if (this.dubEditorTab === "Primary") {
        if (this.dubEditorData?.primaryFragments?.complete && this.changesWereAdded) {
          resubmitAlert = true;
        }
      } else if (this.dubEditorTab === "Secondary") {
        if (this.dubEditorData?.secondaryFragments?.complete && this.changesWereAdded) {
          resubmitAlert = true;
        }
      } else if (this.dubEditorTab === "QC") {
        if (this.dubEditorData?.qcFragments?.complete && this.changesWereAdded) {
          resubmitAlert = true;
        }
      }
      return resubmitAlert;
    },
    checkIfShowResubmitButton() {
      let showButton = false;
      if (this.dubEditorTab === "Primary") {
        if (this.checkIfAllowed) {
          if (this.dubEditorData?.primaryFragments?.complete && !this.changesWereAdded) {
            showButton = true;
          }
        }
      } else if (this.dubEditorTab === "Secondary") {
        if (this.checkIfAllowed) {
          if (this.dubEditorData?.secondaryFragments?.complete && !this.changesWereAdded) {
            showButton = true;
          }
        }
      } else if (this.dubEditorTab === "QC") {
        if (this.checkIfAllowed) {
          if (this.dubEditorData?.qcFragments?.complete && !this.changesWereAdded) {
            showButton = true;
          }
        }
      }
      return showButton;
    },
    checkIfShowSaveDraftAlert() {
      let saveDraftAlert = false;
      if (this.dubEditorTab === "Primary") {
        if (!this.dubEditorData?.primaryFragments?.complete && this.changesWereAdded) {
          saveDraftAlert = true;
        }
      } else if (this.dubEditorTab === "Secondary") {
        if (!this.dubEditorData?.secondaryFragments?.complete && this.changesWereAdded) {
          saveDraftAlert = true;
        }
      } else if (this.dubEditorTab === "QC") {
        if (!this.dubEditorData?.qcFragments?.complete && this.changesWereAdded) {
          saveDraftAlert = true;
        }
      }
      return saveDraftAlert;
    },
    prevIsNotReady() {
      let prevIsNotReady = false;
      if (this.dubEditorTab === "Secondary") {
        prevIsNotReady = !this.dubEditorData?.primaryFragments?.complete;
      } else if (this.dubEditorTab === "QC") {
        prevIsNotReady = !this.dubEditorData?.secondaryFragments?.complete;
      }
      return prevIsNotReady;
    },
    changesWereAdded() {
      if (this.dubEditorTab === "Primary") {
        return !_.isEqual(this.dubEditorData?.primaryFragments, this.dubEditorDataClone?.primaryFragments);
      } else if (this.dubEditorTab === "Secondary") {
        return !_.isEqual(this.dubEditorData?.secondaryFragments, this.dubEditorDataClone?.secondaryFragments);
      } else if (this.dubEditorTab === "QC") {
        return !_.isEqual(this.dubEditorData?.qcFragments, this.dubEditorDataClone?.qcFragments);
      }
      return false;
    },
  },

  watch: {
    "assignmentForm.id": {
      immediate: true,
      handler() {
        this.refreshDubEditor();
      },
    },
    "assignmentForm.relatedProject": {
      handler() {
        // For an unsaved draft, (re)build the empty inputs from the chosen project's DubEditor config.
        if (!this.assignmentForm.id) this.refreshDubEditor();
      },
    },
  },

  methods: {
    ...mapActions("flashMessage", ["handleFlash"]),
    setDubEditorTab(tab) {
      this.dubEditorTab = tab;
    },
    async refreshDubEditor() {
      if (this.assignmentForm.id) {
        await this.prepareEditorData();
        // Saved assignment without DubEditor data: still show fields.
        if (!this.dubEditorData) this.buildEmptyFromProject();
      } else {
        // Draft: always (re)build from the current project's config, or defaults.
        this.buildEmptyFromProject();
      }
    },
    staffName(person) {
      if (!person) return "";
      if (person.user) return `${person.user.firstName ?? ""} ${person.user.lastName ?? ""}`.trim();
      return `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
    },
    buildEmptyFromProject() {
      const projectDefs = this.assignmentForm.relatedProject?.dubEditor?.fragments;
      const defs = projectDefs && projectDefs.length ? projectDefs : DEFAULT_DUB_FRAGMENTS;
      const buildFragments = () =>
        [...defs]
          .sort((a, b) => a.order - b.order)
          .map((def) => ({
            id: def.id,
            fragmentId: def.id,
            fragmentName: def.fragmentName,
            min: def.min,
            max: def.max,
            count: def.count,
            order: def.order,
            text: "",
          }));
      this.dubEditorData = {
        primaryFragments: { fragments: buildFragments(), complete: false, staffName: this.staffName(this.assignmentForm.primary) },
        secondaryFragments: { fragments: buildFragments(), complete: false, staffName: this.staffName(this.assignmentForm.secondary) },
        qcFragments: { fragments: buildFragments(), complete: false, staffName: this.staffName(this.assignmentForm.qc) },
      };
      this.dubEditorDataClone = _.cloneDeep(this.dubEditorData);
      this.dubIsReal = false;
      this.dubChecked = true;
    },
    plainTextFromHtml(html) {
      if (!html) return "";
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    },
    fragmentCount(fragment) {
      const unit = (fragment.count || "").toString().toLowerCase();
      const plainText = this.plainTextFromHtml(fragment.text);
      if (unit === "words") {
        const words = plainText.trim().split(/\s+/);
        return words.filter((word) => word.length > 0).length;
      }
      return plainText.length;
    },
    fragmentTitle(fragment) {
      return `${fragment.fragmentName} (${this.fragmentCount(fragment)}/${fragment.max} ${fragment.count})`;
    },
    onFragmentInput(fragment, html) {
      fragment.text = html;
    },
    async prepareEditorData() {
      try {
        const response = await getAssignmentById(this.assignmentForm.id);
        if (response?.dubEditorAssignment && response?.project?.dubEditor) {
          const dubClone = _.cloneDeep(response.dubEditorAssignment);
        if (dubClone && dubClone.primaryFragments) {
          dubClone.primaryFragments.fragments.forEach((fragment) => {
            fragment.min = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.min;
            fragment.max = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.max;
            fragment.count = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.count;
          });
        }
        if (dubClone && dubClone.secondaryFragments) {
          dubClone.secondaryFragments.fragments.forEach((fragment) => {
            fragment.min = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.min;
            fragment.max = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.max;
            fragment.count = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.count;
          });
          if (
            dubClone.primaryFragments?.fragments.length &&
            dubClone.secondaryFragments?.fragments.length &&
            dubClone.primaryFragments?.complete &&
            dubClone.secondaryFragments?.fragments.map((item) => item.text).every((item) => item === "")
          ) {
            dubClone.primaryFragments?.fragments.forEach((el) => {
              const fragmentItem = dubClone.secondaryFragments && dubClone.secondaryFragments?.fragments.find((item) => item.fragmentId === el.fragmentId);
              if (fragmentItem) {
                fragmentItem.text = el.text;
              }
            });
          }
        }
        if (dubClone && dubClone.qcFragments) {
          dubClone.qcFragments.fragments.forEach((fragment) => {
            fragment.min = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.min;
            fragment.max = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.max;
            fragment.count = response.project.dubEditor.fragments.find((el) => el.id === fragment.fragmentId)?.count;
          });
          if (
            dubClone.secondaryFragments?.fragments.length &&
            dubClone.qcFragments?.fragments.length &&
            dubClone.secondaryFragments?.complete &&
            dubClone.qcFragments?.fragments.map((item) => item.text).every((item) => item === "")
          ) {
            dubClone.secondaryFragments?.fragments.forEach((el) => {
              const fragmentItem = dubClone.qcFragments && dubClone.qcFragments?.fragments.find((item) => item.fragmentId === el.fragmentId);
              if (fragmentItem) {
                fragmentItem.text = el.text;
              }
            });
          }
        }
        ["primaryFragments", "secondaryFragments", "qcFragments"].forEach((item) => {
          dubClone[item]?.fragments?.sort((a, b) => a.order - b.order);
        });
          this.dubEditorData = dubClone;
          this.dubEditorDataClone = _.cloneDeep(this.dubEditorData);
          this.dubIsReal = true;
          this.selectDefaultTab();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.dubChecked = true;
      }
    },
    selectDefaultTab() {
      if (this.user.user.userRole === "STAFF") {
        if (this.dubEditorData.primaryFragments && this.dubEditorData.primaryFragments.staffId === this.user.user.id) {
          this.dubEditorTab = "Primary";
        } else if (this.dubEditorData.secondaryFragments && this.dubEditorData.secondaryFragments.staffId === this.user.user.id) {
          this.dubEditorTab = "Secondary";
        } else if (this.dubEditorData.qcFragments && this.dubEditorData.qcFragments.staffId === this.user.user.id) {
          this.dubEditorTab = "QC";
        }
      }
    },
    async discardChanges() {
      this.dubEditorData = null;
      await this.prepareEditorData();
    },
    async handleDubProcessing(variant) {
      this.dubLoading = true;
      if (variant === "Submit" || variant === "Resubmit") {
        if (this.dubEditorTab === "Primary") {
          this.dubEditorData.primaryFragments.complete = true;
        } else if (this.dubEditorTab === "Secondary") {
          this.dubEditorData.secondaryFragments.complete = true;
        } else if (this.dubEditorTab === "QC") {
          this.dubEditorData.qcFragments.complete = true;
        }
      }
      if (variant === "Resubmit") {
        if (this.dubEditorTab === "Primary") {
          this.dubEditorData.secondaryFragments && this.dubEditorData.secondaryFragments.fragments.length && this.dubEditorData.secondaryFragments.fragments.forEach((el) => {
            el.text = this.dubEditorData.primaryFragments.fragments.find((item) => item.fragmentId === el.fragmentId)?.text;
          });
          if (this.dubEditorData.secondaryFragments) this.dubEditorData.secondaryFragments.complete = false;
          this.dubEditorData.qcFragments && this.dubEditorData.qcFragments.fragments.length && this.dubEditorData.qcFragments.fragments.forEach((el) => {
            el.text = "";
          });
          if (this.dubEditorData.qcFragments) this.dubEditorData.qcFragments.complete = false;
        } else if (this.dubEditorTab === "Secondary") {
          if (this.dubEditorData.qcFragments) this.dubEditorData.qcFragments.complete = false;
          this.dubEditorData.qcFragments && this.dubEditorData.qcFragments.fragments.length && this.dubEditorData.qcFragments.fragments.forEach((el) => {
            el.text = this.dubEditorData.secondaryFragments.fragments.find((item) => item.fragmentId === el.fragmentId)?.text;
          });
        } else if (this.dubEditorTab === "QC") {
          if (this.dubEditorData.qcFragments) this.dubEditorData.qcFragments.complete = true;
        }
      }
      if (variant === "Unsubmit") {
        if (this.dubEditorTab === "Primary") {
          this.dubEditorData.primaryFragments.complete = false;
          ["secondaryFragments", "qcFragments"].forEach((item) => {
            this.dubEditorData && this.dubEditorData[item] && this.dubEditorData[item].fragments.length && this.dubEditorData[item].fragments.forEach((el) => {
              el.text = "";
            });
          });
        } else if (this.dubEditorTab === "Secondary") {
          this.dubEditorData.secondaryFragments.complete = false;
          this.dubEditorData && this.dubEditorData.qcFragments && this.dubEditorData.qcFragments.fragments.length && this.dubEditorData.qcFragments.fragments.forEach((el) => {
            el.text = "";
          });
        } else if (this.dubEditorTab === "QC") {
          this.dubEditorData.qcFragments.complete = false;
        }
      }
      this.confirmationDubSubmition = this.confirmationDubReSubmition = this.confirmationDubUnSubmition = false;
      const response = await processDubEditor(this.dubEditorData);
      if (response.status === 200) {
        this.handleFlash({
          response: {
            data: {
              message:
                variant === "Submit"
                  ? "Text submitted successfully."
                  : variant === "Resubmit"
                  ? "Text resubmitted successfully."
                  : "Draft saved.",
              statusCode: 200,
            },
          },
          show: true,
        });
      }
      await this.prepareEditorData();
      this.dubLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.addFormDubEditorTab {
  width: 100%;
  &__tabs {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
  }
  &__fragments {
    display: flex;
    flex-direction: column;
  }
  &__empty {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #B4B4B4;
    margin: 20px 0;
  }
  &__actions {
    margin-top: 15px;
    justify-content: space-between;
    &--alert {
      flex: 1;
    }
  }
}

.dub-tab-btn {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 4px 14px;
  border: 2px solid #1976D2CC;
  border-radius: 0;
  background-color: #fff;
  cursor: pointer;
  &--rounded {
    border-radius: 8px;
  }
  &--rounded-left {
    border-radius: 8px 0 0 8px;
    margin-right: 2px;
  }
  &--square {
    border-radius: 0;
    margin-right: 2px;
  }
  &--rounded-right {
    border-radius: 0 8px 8px 0;
  }
  &.active {
    background-color: #1976D2;
    p {
      color: #fff !important;
    }
  }
  &__label {
    font-family: Roboto;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.0089em;
    color: #4791DB;
    text-align: left;
    margin: 0;
  }
  &__name {
    font-family: Roboto;
    font-size: 13px;
    font-weight: 300;
    letter-spacing: 0.0089em;
    color: #4791DB;
    text-align: left;
    margin: 0;
  }
  &__icon {
    color: #1976D2;
    font-weight: 400;
    &.active {
      color: #fff;
    }
  }
}

.confirmation-modal {
  background-color: #fff !important;
  width: 500px !important;
  display: flex;
  flex-direction: column;
  padding: 30px;
  &--title {
    color: #000000 !important;
    font-weight: 500 !important;
    font-size: 20px !important;
  }
  &--text {
    color: #000000 !important;
    font-weight: 300 !important;
    font-size: 14px !important;
  }
  &--buttons {
    margin-top: 20px;
    font-family: 'Roboto', sans-serif !important;
    font-weight: 700 !important;
    font-size: 14px !important;
  }
}

.mb-15px {
  margin-bottom: 15px;
}
</style>
